import classNames from 'classnames';
import clamp from 'lodash/clamp';
import * as React from 'react';
import type { Theme } from '~/Utils/Theme';
import { themeDefaultValues } from '~/Utils/Theme';
import styles from './Picture.module.css';
import { constructImageSharpUrl } from './Utils/constructImageSharpUrl';

export interface PictureSource {
    allowedWidths: number[];
    sizes: string[];
}

export interface PictureProfile {
    sources: PictureSource[];
    defaultWidth: number;
}

export interface FocalPoint {
    x: number;
    y: number;
}

export interface BaseImage {
    url: string;
    alt: string;
    focalPoint?: FocalPoint;
    lazyLoading?: boolean;
}

export interface PictureDefinition extends BaseImage {
    profile: PictureProfile;
}

export interface PictureProps extends PictureDefinition {
    theme?: Pick<Theme, 'withRoundedCorners'>;
    className?: string;
}

export const Picture: React.FC<React.PropsWithChildren<PictureProps>> = props => {
    const { url, alt, focalPoint, profile, className, theme = themeDefaultValues, lazyLoading = true } = props;

    const { withRoundedCorners } = theme;

    const createPicture = (pictureUrl: string, pictureAlt: string, pictureProfile: PictureProfile) => {
        const [mainSource] = pictureProfile.sources;
        const sourceSizes = mainSource.sizes.join(', ');
        const sourceSrcSets = mainSource.allowedWidths
            .map(width => `${constructImageSharpUrl(pictureUrl, width)} ${width}w`)
            .join(', ');

        const getPosition = (point: FocalPoint) => {
            const xPosition = clamp(point.x, 0, 1);
            const yPosition = clamp(point.y, 0, 1);

            return `${xPosition * 100}% ${yPosition * 100}%`;
        };

        return (
            <picture>
                <source sizes={sourceSizes} srcSet={sourceSrcSets} />
                <img
                    src={constructImageSharpUrl(pictureUrl, pictureProfile.defaultWidth)}
                    alt={pictureAlt}
                    style={focalPoint && { objectPosition: getPosition(focalPoint) }}
                    loading={lazyLoading ? 'lazy' : 'eager'}
                />
            </picture>
        );
    };

    return (
        <div className={classNames(styles.Picture, withRoundedCorners && styles['Variant--rounded'], className)}>
            {createPicture(url, alt, profile)}
        </div>
    );
};
